/**
 * Dress & one-piece silhouettes: dress, jumpsuit, romper, gown, playsuit.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { partGroup } from "./_part-attrs.js";
import { neckLabel } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Dress (A-line, sleeveless, fitted bodice + flared skirt) ─────── */
export function dressFlat(view: FlatView): string {
  const neckDip = view === "front" ? 120 : 98;
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 156 82 L 118 100 Q 98 118 108 142 Q 118 154 134 148 L 138 242 L 100 440 Q 200 454 300 440 L 262 242 L 266 148 Q 282 154 292 142 Q 302 118 282 100 L 244 82 Q 200 ${neckDip - 20} 156 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 138 242 L 262 242"/>
  <path ${STITCH_ATTRS} d="M 140 246 L 260 246"/>
  ${view === "front"
    ? `<path ${STITCH_ATTRS} d="M 158 240 L 164 172"/>
  <path ${STITCH_ATTRS} d="M 242 240 L 236 172"/>`
    : `<path ${DETAIL_ATTRS} d="M 200 92 L 200 434"/>
  <path ${STITCH_ATTRS} d="M 160 238 L 166 166"/>
  <path ${STITCH_ATTRS} d="M 240 238 L 234 166"/>
  ${neckLabel(200, 82)}`}`)}
  ${partGroup("collar", `<path ${DETAIL_ATTRS} d="M 156 82 Q 200 ${neckDip} 244 82"/>
  <path ${STITCH_ATTRS} d="M 159 87 Q 200 ${neckDip + 6} 241 87"/>`)}
  ${partGroup("sleeve_left", `<path ${STITCH_ATTRS} d="M 102 136 Q 114 148 130 144"/>
  <path ${STITCH_ATTRS} d="M 118 102 Q 128 124 134 148"/>`)}
  ${partGroup("sleeve_right", `<path ${STITCH_ATTRS} d="M 298 136 Q 286 148 270 144"/>
  <path ${STITCH_ATTRS} d="M 282 102 Q 272 124 266 148"/>`)}
  ${partGroup("hem", `<path ${STITCH_ATTRS} d="M 106 432 Q 200 446 294 432"/>
  <path ${STITCH_ATTRS} d="M 104 436 Q 200 450 296 436"/>`)}
</g>`;
}

/* ── Jumpsuit (fitted bodice + full-length legs, waist seam) ──────── */
export function jumpsuitFlat(view: FlatView): string {
  const neckDip = view === "front" ? 118 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 82 L 120 100 Q 100 116 110 140 Q 120 152 136 146 L 140 232 L 142 248 L 142 440 L 196 440 L 198 286 Q 200 274 202 286 L 204 440 L 258 440 L 260 248 L 260 232 L 264 146 Q 280 152 290 140 Q 300 116 280 100 L 242 82 Q 200 ${neckDip - 20} 158 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 158 82 Q 200 ${neckDip} 242 82"/>
  <path ${STITCH_ATTRS} d="M 161 87 Q 200 ${neckDip + 6} 239 87"/>
  <path ${STITCH_ATTRS} d="M 104 134 Q 116 148 132 142"/>
  <path ${STITCH_ATTRS} d="M 296 134 Q 284 148 268 142"/>
  <path ${STITCH_ATTRS} d="M 120 102 Q 130 124 136 146"/>
  <path ${STITCH_ATTRS} d="M 280 102 Q 270 124 264 146"/>
  <path ${DETAIL_ATTRS} d="M 140 248 L 260 248"/>
  <path ${STITCH_ATTRS} d="M 142 252 L 258 252"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 248 L 200 278"/>
  <path ${STITCH_ATTRS} d="M 195 250 Q 192 264 198 280"/>
  <path ${STITCH_ATTRS} d="M 160 244 L 164 174"/>
  <path ${STITCH_ATTRS} d="M 240 244 L 236 174"/>`
      : `<path ${DETAIL_ATTRS} d="M 200 92 L 200 434"/>
  <path ${STITCH_ATTRS} d="M 160 244 L 166 174"/>
  <path ${STITCH_ATTRS} d="M 240 244 L 234 174"/>
  ${neckLabel(200, 82)}`
  }
  <path ${STITCH_ATTRS} d="M 144 432 L 194 432"/>
  <path ${STITCH_ATTRS} d="M 206 432 L 256 432"/>
</g>`;
}

/* ── Romper (fitted bodice + shorts, waist seam) ─────────────────── */
export function romperFlat(view: FlatView): string {
  const neckDip = view === "front" ? 118 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 82 L 120 100 Q 100 116 110 140 Q 120 152 136 146 L 140 232 L 140 248 L 138 340 L 192 340 L 198 278 Q 200 268 202 278 L 208 340 L 262 340 L 260 248 L 260 232 L 264 146 Q 280 152 290 140 Q 300 116 280 100 L 242 82 Q 200 ${neckDip - 20} 158 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 158 82 Q 200 ${neckDip} 242 82"/>
  <path ${STITCH_ATTRS} d="M 161 87 Q 200 ${neckDip + 6} 239 87"/>
  <path ${STITCH_ATTRS} d="M 104 134 Q 116 148 132 142"/>
  <path ${STITCH_ATTRS} d="M 296 134 Q 284 148 268 142"/>
  <path ${STITCH_ATTRS} d="M 120 102 Q 130 124 136 146"/>
  <path ${STITCH_ATTRS} d="M 280 102 Q 270 124 264 146"/>
  <path ${DETAIL_ATTRS} d="M 140 248 L 260 248"/>
  <path ${STITCH_ATTRS} d="M 142 252 L 258 252"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 248 L 200 272"/>
  <path ${STITCH_ATTRS} d="M 196 250 Q 194 260 198 274"/>
  <path ${STITCH_ATTRS} d="M 160 244 L 164 174"/>
  <path ${STITCH_ATTRS} d="M 240 244 L 236 174"/>`
      : `<path ${DETAIL_ATTRS} d="M 200 92 L 200 332"/>
  <path ${STITCH_ATTRS} d="M 160 244 L 166 174"/>
  <path ${STITCH_ATTRS} d="M 240 244 L 234 174"/>
  ${neckLabel(200, 82)}`
  }
  <path ${STITCH_ATTRS} d="M 140 332 L 190 332"/>
  <path ${STITCH_ATTRS} d="M 210 332 L 260 332"/>
</g>`;
}

/* ── Gown (floor-length, more formal, wider flare) ────────────────── */
export function gownFlat(view: FlatView): string {
  const neckDip = view === "front" ? 122 : 100;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 154 82 L 116 100 Q 96 118 106 142 Q 116 154 132 148 L 136 230 L 82 460 Q 200 476 318 460 L 264 230 L 268 148 Q 284 154 294 142 Q 304 118 284 100 L 246 82 Q 200 ${neckDip - 22} 154 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 154 82 Q 200 ${neckDip} 246 82"/>
  <path ${STITCH_ATTRS} d="M 157 87 Q 200 ${neckDip + 6} 243 87"/>
  <path ${STITCH_ATTRS} d="M 100 136 Q 112 148 128 144"/>
  <path ${STITCH_ATTRS} d="M 300 136 Q 288 148 272 144"/>
  <path ${STITCH_ATTRS} d="M 116 102 Q 126 124 132 148"/>
  <path ${STITCH_ATTRS} d="M 284 102 Q 274 124 268 148"/>
  <path ${DETAIL_ATTRS} d="M 136 230 L 264 230"/>
  <path ${STITCH_ATTRS} d="M 138 234 L 262 234"/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 156 228 L 162 168"/>
  <path ${STITCH_ATTRS} d="M 244 228 L 238 168"/>`
      : `<path ${DETAIL_ATTRS} d="M 200 92 L 200 456"/>
  <path ${STITCH_ATTRS} d="M 158 226 L 164 164"/>
  <path ${STITCH_ATTRS} d="M 242 226 L 236 164"/>
  ${neckLabel(200, 82)}`
  }
  <path ${STITCH_ATTRS} d="M 88 452 Q 200 468 312 452"/>
  <path ${STITCH_ATTRS} d="M 86 456 Q 200 472 314 456"/>
</g>`;
}

/* ── Playsuit (casual romper, wider neckline, relaxed fit) ─────────── */
export function playsuitFlat(view: FlatView): string {
  const neckDip = view === "front" ? 124 : 100;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 154 82 L 114 100 Q 94 118 104 142 Q 114 154 130 148 L 136 240 L 132 248 L 128 340 L 186 340 L 196 282 Q 200 272 204 282 L 214 340 L 272 340 L 268 248 L 264 240 L 270 148 Q 286 154 296 142 Q 306 118 286 100 L 246 82 Q 200 ${neckDip - 22} 154 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 154 82 Q 200 ${neckDip} 246 82"/>
  <path ${STITCH_ATTRS} d="M 157 87 Q 200 ${neckDip + 6} 243 87"/>
  <path ${STITCH_ATTRS} d="M 98 136 Q 110 148 126 144"/>
  <path ${STITCH_ATTRS} d="M 302 136 Q 290 148 274 144"/>
  <path ${STITCH_ATTRS} d="M 114 102 Q 124 124 130 148"/>
  <path ${STITCH_ATTRS} d="M 286 102 Q 276 124 270 148"/>
  <path ${DETAIL_ATTRS} d="M 132 248 L 268 248"/>
  <path ${STITCH_ATTRS} d="M 134 252 L 266 252"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 248 L 200 276"/>
  <path ${STITCH_ATTRS} d="M 196 250 Q 194 262 198 278"/>
  <path ${STITCH_ATTRS} d="M 156 244 L 162 174"/>
  <path ${STITCH_ATTRS} d="M 244 244 L 238 174"/>`
      : `<path ${DETAIL_ATTRS} d="M 200 92 L 200 334"/>
  <path ${STITCH_ATTRS} d="M 158 244 L 164 174"/>
  <path ${STITCH_ATTRS} d="M 242 244 L 236 174"/>
  ${neckLabel(200, 82)}`
  }
  <path ${STITCH_ATTRS} d="M 130 332 L 184 332"/>
  <path ${STITCH_ATTRS} d="M 216 332 L 270 332"/>
</g>`;
}
